// A day's weather in words, for the Days view (features/weather.md): °C, or °F where the browser's
// region uses it; how often it usually rains as "1 day in 4"; what a tap says it is. Plain functions;
// DayWeather.tsx draws them.
import type { WeatherDay, WeatherSymbol } from "../../src/web/types.ts";

export type Unit = "C" | "F";

// Where people read temperatures in Fahrenheit: CLDR's list (the United States, Puerto Rico, the
// Bahamas, Belize, the Cayman Islands and Palau) and the United States' other territories, by country,
// and by the time zones only they have.
const FAHRENHEIT = new Set(["US", "PR", "GU", "VI", "AS", "MP", "UM", "BS", "BZ", "KY", "PW"]);
const FAHRENHEIT_ZONES =
  /^(US\/.+|America\/(New_York|Detroit|Chicago|Denver|Phoenix|Los_Angeles|Anchorage|Juneau|Sitka|Metlakatla|Yakutat|Nome|Adak|Boise|Menominee|Indianapolis|Fort_Wayne|Louisville|Shiprock|Puerto_Rico|St_Thomas|Virgin|Nassau|Belize|Cayman|(Indiana|Kentucky|North_Dakota)\/.+)|Pacific\/(Honolulu|Johnston|Guam|Saipan|Pago_Pago|Samoa|Midway|Wake|Palau))$/;

// °C or °F by the browser's region. Its time zone says where it is (a phone sets it by itself; many
// browsers outside the United States still speak "en-US"); with none to go by (UTC), the first of its
// languages that names a region ("en-SG"), else its first language's likely one ("en" is "en-US").
export function unitFor({ timeZone, languages }: { timeZone: string | undefined; languages: readonly string[] }): Unit {
  if (timeZone && !/^(UTC|GMT|Etc\/|Factory)/.test(timeZone)) return FAHRENHEIT_ZONES.test(timeZone) ? "F" : "C";
  const region = (tag: string, likely: boolean) => {
    try {
      const locale = new Intl.Locale(tag);
      return (likely ? locale.maximize() : locale).region ?? null;
    } catch {
      return null;
    }
  };
  const named =
    languages.map((l) => region(l, false)).find(Boolean) ?? (languages[0] ? region(languages[0], true) : null);
  return named && FAHRENHEIT.has(named) ? "F" : "C";
}

// A temperature, whole degrees in the unit, with a true minus sign: "13°", "−2°".
export function degrees(celsius: number, unit: Unit): string {
  const n = Math.round(unit === "F" ? (celsius * 9) / 5 + 32 : celsius);
  return `${n < 0 ? "−" : ""}${Math.abs(n)}°`;
}

// Rain a forecast brings: none under 0.1 mm, "<1 mm" under a millimetre, else whole millimetres.
export function rainAmount(mm: number): string | null {
  if (mm < 0.1) return null;
  return mm < 1 ? "<1 mm" : `${Math.round(mm)} mm`;
}

// How often it usually rains, as a share of days: the nearest plain "1 day in 4" ("2 days in 5"), with a
// simpler one preferred when it's nearly as near.
export function rainOdds(share: number): string {
  if (share < 0.04) return "rarely rains";
  if (share >= 0.95) return "rain nearly every day";
  let best = { a: 1, b: 2, cost: Number.POSITIVE_INFINITY };
  for (let b = 2; b <= 30; b++) {
    for (let a = 1; a < b; a++) {
      if (gcd(a, b) !== 1 || (b > 10 && a > 1)) continue;
      const cost = Math.abs(a / b - share) + 0.01 * Math.min(b, 10);
      if (cost < best.cost) best = { a, b, cost };
    }
  }
  return `rain ${best.a} ${best.a === 1 ? "day" : "days"} in ${best.b}`;
}

const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

export const SYMBOL_LABEL: Record<WeatherSymbol, string> = {
  clear: "Sunny",
  partly_cloudy: "Partly cloudy",
  cloudy: "Cloudy",
  fog: "Fog",
  light_rain: "Light rain",
  rain: "Rain",
  heavy_rain: "Heavy rain",
  sleet: "Sleet",
  snow: "Snow",
  thunder: "Thunder",
};

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// "early December", "mid-December", "late December": the time of year Usually stands for.
export function timeOfYear(date: string): string {
  const day = Number(date.slice(8, 10));
  const month = MONTHS[Number(date.slice(5, 7)) - 1] ?? "";
  return day <= 10 ? `early ${month}` : day <= 20 ? `mid-${month}` : `late ${month}`;
}

// The date it is now at a place in time zone `tz`.
export function todayIn(tz: string, now: Date): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(now);
    const part = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
    return `${part("year")}-${part("month")}-${part("day")}`;
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

// More than a day old, a forecast says when it's from.
export const OLD_MS = 86_400_000;

// When a forecast was looked up, as people read it: "07:10" today, else "Mon 7 Dec, 07:10".
export function lookedUp(at: Date, now: Date): string {
  const time = at.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  if (at.toDateString() === now.toDateString()) return time;
  return `${at.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}, ${time}`;
}

// The day's weather from today on, by each place's own calendar: what the phone kept from days ago
// drops its days gone by.
export function current(days: WeatherDay[], now: Date): Map<string, WeatherDay> {
  return new Map(days.filter((d) => d.date >= todayIn(d.tz, now)).map((d) => [d.date, d]));
}
